// src/admin/FaqEditor.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

export default function FaqEditor({
  baseURL = "https://gantasoft.in/rankridgecollege/dev/v1",
  apiKey = "12345678",
  initial = {
    faq_id: null,
    page_id: null,
    subpage_id: null,
    question: "",
    answer: "",
    sort_order: 0,
    status: "active",
  },
  onClose,
  onSaved,
}) {
  const api = useMemo(
    () =>
      axios.create({
        baseURL,
        headers: { "X-API-Key": apiKey },
      }),
    [baseURL, apiKey]
  );

  const [form, setForm] = useState({ ...initial });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [pages, setPages] = useState([]);
  const [subpages, setSubpages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(true);
  const [loadingSubpages, setLoadingSubpages] = useState(false);

  // pages
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        setLoadingPages(true);
        const { data } = await api.get("/pages", {
          params: { status: "active", sort: "page_id", order: "asc", limit: 1000 },
        });
        if (!dead) setPages(data?.items || []);
      } catch (e) {
        if (!dead) setError(e?.response?.data?.message || e.message || "Failed to load pages");
      } finally {
        if (!dead) setLoadingPages(false);
      }
    })();
    return () => { dead = true; };
  }, [api]);

  // subpages for selected page
  useEffect(() => {
    let dead = false;
    (async () => {
      if (!form.page_id) {
        setSubpages([]);
        setForm(f => ({ ...f, subpage_id: null }));
        return;
      }
      try {
        setLoadingSubpages(true);
        const { data } = await api.get("/subpages", {
          params: { page_id: form.page_id, status: "active", sort: "subpage_id", order: "asc", limit: 1000 },
        });
        if (!dead) setSubpages(data?.items || []);
        // clear if not in list
        if (!dead && form.subpage_id) {
          const ok = (data?.items || []).some(s => Number(s.subpage_id) === Number(form.subpage_id));
          if (!ok) setForm(f => ({ ...f, subpage_id: null }));
        }
      } catch (e) {
        if (!dead) setError(e?.response?.data?.message || e.message || "Failed to load subpages");
      } finally {
        if (!dead) setLoadingSubpages(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, form.page_id]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.question.trim()) return "Question is required.";
    if (!form.answer.trim()) return "Answer is required.";
    if (!form.page_id && !form.subpage_id) return "Select a Page or a Subpage.";
    return "";
  };

  const save = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true);
    setError("");
    try {
      const payload = {
        page_id: form.page_id ?? null,
        subpage_id: form.subpage_id ?? null,
        question: form.question,
        answer: form.answer,
        sort_order: Number(form.sort_order) || 0,
        status: form.status || "active",
      };
      if (form.faq_id) {
        await api.put(`/faqs/${form.faq_id}`, { faq_id: form.faq_id, ...payload });
      } else {
        await api.post(`/faqs`, payload);
      }
      onSaved && onSaved();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold">{form.faq_id ? "Edit FAQ" : "Add FAQ"}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        <div className="p-4 grid gap-4">
          {error && <div className="px-3 py-2 text-sm rounded bg-red-50 text-red-700 border border-red-200">{error}</div>}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-700">Page</label>
              <select
                className="border rounded px-3 py-2 w-full"
                value={form.page_id ?? ""}
                onChange={(e)=>upd("page_id", e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">— Select Page —</option>
                {loadingPages ? <option disabled>Loading…</option> :
                  pages.map(p => <option key={p.page_id} value={p.page_id}>[{p.page_id}] {p.page_name} — {p.page_slug}</option>)
                }
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-700">Subpage (optional)</label>
              <select
                className="border rounded px-3 py-2 w-full"
                value={form.subpage_id ?? ""}
                onChange={(e)=>upd("subpage_id", e.target.value ? Number(e.target.value) : null)}
                disabled={!form.page_id || loadingSubpages}
              >
                <option value="">
                  {form.page_id ? (loadingSubpages ? "Loading…" : "— Select Subpage —") : "Select a Page first"}
                </option>
                {subpages.map(sp => (
                  <option key={sp.subpage_id} value={sp.subpage_id}>
                    [{sp.subpage_id}] {sp.subpage_name} — {sp.subpage_slug}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-700">Question *</label>
            <input
              className="border rounded px-3 py-2 w-full"
              value={form.question}
              onChange={(e)=>upd("question", e.target.value)}
              placeholder="e.g., What are school timings?"
            />
          </div>

          <div>
            <label className="text-sm text-gray-700">Answer *</label>
            <textarea
              rows={8}
              className="border rounded px-3 py-2 w-full"
              value={form.answer}
              onChange={(e)=>upd("answer", e.target.value)}
              placeholder="Provide the detailed answer here."
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-700">Sort Order</label>
              <input
                type="number"
                className="border rounded px-3 py-2 w-full"
                value={form.sort_order ?? 0}
                onChange={(e)=>upd("sort_order", Number(e.target.value))}
              />
            </div>
            <div>
              <label className="text-sm text-gray-700">Status</label>
              <select
                className="border rounded px-3 py-2 w-full"
                value={form.status}
                onChange={(e)=>upd("status", e.target.value)}
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
                <option value="deleted">deleted</option>
              </select>
            </div>
          </div>
        </div>

        <div className="p-4 border-t flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded bg-green-700 text-white disabled:opacity-60">
            {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="px-4 py-2 rounded bg-gray-200">Cancel</button>
        </div>
      </div>
    </div>
  );
}
